import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth, provider } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

// CSS Variables matching app-mothai.js theme
const CSS_VARS = {
  gold: '#D4AF37',
  goldLight: '#F4E4BA',
  goldDark: '#996515',
  greenDeep: '#1A3C34',
  greenForest: '#2D5A4A',
  greenHerb: '#4A7C59',
  greenLeaf: '#6B9B7A',
  cream: '#FDF8F0',
  creamDark: '#E8DFD0',
  white: '#FFFFFF',
  black: '#0D1B16',
};

// Add CSS animation for logo
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.setAttribute('data-logo-animation-login', 'true');
  styleSheet.innerText = `
    @keyframes logoFloat {
      0%, 100% {
        transform: translateY(0px) rotate(0deg);
      }
      25% {
        transform: translateY(-10px) rotate(2deg);
      }
      50% {
        transform: translateY(-20px) rotate(0deg);
      }
      75% {
        transform: translateY(-10px) rotate(-2deg);
      }
    }
    
    .logo-hero {
      animation: logoFloat 6s ease-in-out infinite;
    }
  `;
  if (!document.head.querySelector('style[data-logo-animation-login]')) {
    document.head.appendChild(styleSheet);
  }
}

const LoginNew = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' or 'google'
  const [formData, setFormData] = useState({
    emailOrPhone: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  
  // Phone login states
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = React.useRef(null);

  // Load saved phone number (if any)
  useEffect(() => {
    const savedPhone = localStorage.getItem('rememberedPhone');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedRememberMe && savedPhone) {
      setFormData(prev => ({
        ...prev,
        emailOrPhone: savedPhone
      }));
      setRememberMe(true);
    }
  }, []);

  // Initialize reCAPTCHA for phone login
  useEffect(() => {
    if (loginMethod === 'phone' && !recaptchaVerifierRef.current) {
      const container = document.createElement('div');
      container.id = 'recaptcha-container-login';
      container.style.display = 'none';
      document.body.appendChild(container);
      
      try {
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-login', {
          'size': 'invisible'
        });
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
      }
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          console.error('Error clearing reCAPTCHA:', error);
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, [loginMethod]);

  // ถ้าล็อกอินแล้ว redirect ไปหน้า pharma-quiz
  useEffect(() => {
    if (currentUser) {
      navigate('/pharma-quiz', { replace: true });
    }
  }, [currentUser, navigate]);

  // Check if input is email or phone
  const isEmail = (input) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
  };

  // Find user by phone number
  const findUserByPhone = async (phone) => {
    try {
      const formattedPhone = phone.startsWith('0') 
        ? `+66${phone.substring(1)}` 
        : phone.startsWith('+66')
        ? phone
        : `+66${phone}`;
      
      const usersQuery = query(
        collection(db, 'users'),
        where('phoneNumber', '==', formattedPhone)
      );
      const snapshot = await getDocs(usersQuery);
      
      if (!snapshot.empty) {
        const userDoc = snapshot.docs[0];
        return userDoc.data().email;
      }
      return null;
    } catch (error) {
      console.error('Error finding user by phone:', error);
      return null;
    }
  };

  // Handle phone login
  const handleSendOTP = async () => {
    if (!formData.emailOrPhone || formData.emailOrPhone.length < 10) {
      setError('กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const formattedPhone = formData.emailOrPhone.startsWith('0') 
        ? `+66${formData.emailOrPhone.substring(1)}` 
        : formData.emailOrPhone.startsWith('+66')
        ? formData.emailOrPhone
        : `+66${formData.emailOrPhone}`;

      if (!recaptchaVerifierRef.current) {
        const container = document.createElement('div');
        container.id = 'recaptcha-container-login';
        container.style.display = 'none';
        document.body.appendChild(container);
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-login', {
          'size': 'invisible'
        });
      }

      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );
      
      setConfirmationResult(confirmation);
      setOtpSent(true);
      setLoading(false);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setError('เกิดข้อผิดพลาดในการส่ง OTP');
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('กรุณากรอก OTP ให้ครบ 6 หลัก');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await confirmationResult.confirm(otp);
      
      // Save phone if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedPhone', formData.emailOrPhone);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedPhone');
        localStorage.removeItem('rememberMe');
      }
      
      // Navigation จะถูกจัดการโดย useEffect
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.code === 'auth/invalid-verification-code') {
        setError('OTP ไม่ถูกต้อง');
      } else if (error.code === 'auth/code-expired') {
        setError('OTP หมดอายุแล้ว กรุณาขอ OTP ใหม่');
        setOtpSent(false);
        setOtp('');
      } else {
        setError('เกิดข้อผิดพลาดในการยืนยัน OTP');
      }
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await signInWithPopup(auth, provider);
      
      // ตรวจสอบว่ามี user document ใน Firestore หรือไม่
      if (result.user) {
        const userRef = doc(db, 'users', result.user.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          // ถ้ายังไม่มี user document → redirect ไปหน้า Register Nurse เพื่อลงทะเบียนต่อ
          console.log('User not registered yet, redirecting to nurse register...');
          navigate('/register?complete=true', { 
            state: { 
              fromGoogle: true,
              email: result.user.email,
              displayName: result.user.displayName 
            } 
          });
          return;
        }
      }
      
      // Navigation จะถูกจัดการโดย useEffect (ถ้ามี user document แล้ว)
    } catch (error) {
      console.error('Google login error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('การล็อกอินถูกยกเลิก');
      } else if (error.code === 'auth/popup-blocked') {
        setError('เบราว์เซอร์บล็อก popup กรุณาอนุญาต popup');
      } else {
        setError('เกิดข้อผิดพลาดในการล็อกอินด้วย Google');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: CSS_VARS.cream,
      fontFamily: "'Sarabun', sans-serif"
    }}>
      {/* Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="w-8 h-8 md:w-10 md:h-10 object-contain transition-transform duration-300 hover:scale-110"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(37, 99, 235, 0.2))'
                }}
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-400 via-pink-400 to-purple-400 rounded-lg flex items-center justify-center shadow-md hidden">
                <span className="text-xl md:text-2xl">🩺</span>
              </div>
              <h1 className="text-lg md:text-xl font-bold" style={{
                background: `linear-gradient(to right, ${CSS_VARS.greenDeep}, ${CSS_VARS.gold})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: "'Kanit', sans-serif"
              }}>
                ติวหมอไทย
              </h1>
            </div>

            {/* Back Link */}
            <Link 
              to="/" 
              className="inline-flex items-center text-sm md:text-base transition"
              style={{ color: '#666' }}
              onMouseEnter={(e) => {
                e.target.style.color = CSS_VARS.greenDeep;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#666';
              }}
            >
              <span className="text-xl md:text-2xl mr-1 md:mr-2">←</span>
              <span>กลับ</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-14 md:pt-20">
        {/* Hero Section */}
        <section className="px-3 md:px-4 pt-2 md:pt-8 pb-3 md:pb-12 flex-shrink-0">
          <div className="container mx-auto">
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {/* Hero Background with Gradient and Vector Pattern */}
              <div 
                className="relative min-h-[400px] md:min-h-[500px] overflow-hidden py-8 md:py-12"
                style={{
                  background: `linear-gradient(165deg, ${CSS_VARS.greenDeep} 0%, ${CSS_VARS.greenForest} 50%, ${CSS_VARS.greenHerb} 100%)`
                }}
              >
                {/* Elegant Vector Pattern - Blue and White */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800">
                    {/* Geometric Pattern */}
                    <defs>
                      <pattern id="geometric-pattern-login" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                        <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                        <circle cx="20" cy="20" r="1.5" fill="white" opacity="0.2"/>
                        <circle cx="80" cy="80" r="1.5" fill="white" opacity="0.2"/>
                        <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                        <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                      </pattern>
                      <pattern id="wave-pattern-login" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                        <path d="M0,100 Q50,50 100,100 T200,100" stroke="white" strokeWidth="1" fill="none" opacity="0.15"/>
                        <path d="M0,150 Q50,100 100,150 T200,150" stroke="white" strokeWidth="1" fill="none" opacity="0.1"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#geometric-pattern-login)"/>
                    <rect width="100%" height="100%" fill="url(#wave-pattern-login)"/>
                    
                    {/* Floating Circles */}
                    <circle cx="100" cy="150" r="60" fill="white" opacity="0.05"/>
                    <circle cx="1100" cy="650" r="80" fill="white" opacity="0.05"/>
                    <circle cx="600" cy="100" r="40" fill="white" opacity="0.08"/>
                    <circle cx="900" cy="300" r="50" fill="white" opacity="0.06"/>
                    <circle cx="200" cy="600" r="70" fill="white" opacity="0.04"/>
                    
                    {/* Elegant Lines */}
                    <line x1="0" y1="200" x2="1200" y2="200" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="400" x2="1200" y2="400" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="600" x2="1200" y2="600" stroke="white" strokeWidth="1" opacity="0.1"/>
                  </svg>
                </div>
                
                {/* Subtle Overlay Gradient */}
                <div className="absolute inset-0" style={{
                  background: `linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)`
                }}></div>

                {/* Floating Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-10 left-10 text-white/20 text-6xl animate-pulse">🌿</div>
                  <div className="absolute top-32 right-20 text-white/20 text-5xl animate-pulse delay-1000">📚</div>
                  <div className="absolute bottom-20 left-20 text-white/20 text-4xl animate-pulse delay-2000">⭐</div>
                  <div className="absolute bottom-32 right-10 text-white/20 text-6xl animate-pulse delay-3000">🏆</div>
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-3 md:px-4 text-white py-4 md:py-8">
                  <div className="mb-4 md:mb-6">
                    {/* Logo with enhanced styling */}
                    <div className="relative inline-block">
                      <img 
                        src="/logo.png" 
                        alt="NurseApp Logo" 
                        className="mx-auto mb-2 md:mb-4 object-contain logo-hero"
                        style={{ 
                          width: 'clamp(120px, 30vw, 200px)',
                          height: 'clamp(120px, 30vw, 200px)',
                          maxWidth: '200px',
                          maxHeight: '200px',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          filter: 'drop-shadow(0 10px 30px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 50px rgba(37, 99, 235, 0.3))',
                          animation: 'logoFloat 6s ease-in-out infinite'
                        }}
                        onError={(e) => {
                          // Fallback to emoji if image not found
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="mx-auto mb-4 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30 hidden" style={{
                        width: 'clamp(120px, 30vw, 200px)',
                        height: 'clamp(120px, 30vw, 200px)',
                        maxWidth: '200px',
                        maxHeight: '200px'
                      }}>
                        <span className="text-5xl md:text-6xl lg:text-7xl">🩺</span>
                      </div>
                    </div>
                  </div>
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 drop-shadow-lg" style={{
                    fontFamily: "'Kanit', sans-serif"
                  }}>
                    ติวหมอไทย
                  </h2>
                  <p className="text-base md:text-xl lg:text-2xl mb-2 md:mb-4 drop-shadow-md opacity-95 font-medium">
                    ยินดีต้อนรับกลับมา
                  </p>
                  <p className="text-sm md:text-lg lg:text-xl mb-2 md:mb-4 drop-shadow-md opacity-90">
                    เข้าสู่ระบบเพื่อเริ่มใช้งาน
                  </p>
                  
                  {/* Gamification Quiz CTA */}
                  <div className="mt-4 md:mt-6">
                    <Link
                      to="/pharma-quiz"
                      className="inline-flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-white font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                      style={{
                        background: `linear-gradient(135deg, ${CSS_VARS.gold} 0%, ${CSS_VARS.goldDark} 100%)`,
                        fontFamily: "'Kanit', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.boxShadow = `0 20px 40px rgba(212, 175, 55, 0.4)`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.2)';
                      }}
                    >
                      <span className="text-xl md:text-2xl">📚</span>
                      <span>ติวข้อสอบแพทย์แผนไทย</span>
                      <span className="text-xs md:text-sm bg-white/30 px-2 py-1 rounded-full">72 ข้อ</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Login Form Section */}
        <section className="px-3 md:px-4 py-2 md:py-8 flex-shrink-0">
          <div className="container mx-auto">
            <div className="max-w-md mx-auto w-full">

          {/* Login Method Selection */}
          <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md space-y-2 md:space-y-4 mb-3 md:mb-4" style={{ backgroundColor: CSS_VARS.white }}>
            <div className="grid grid-cols-2 gap-2 md:gap-4">
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('google');
                  setOtpSent(false);
                  setOtp('');
                  setConfirmationResult(null);
                }}
                className="p-2 md:p-4 border-2 rounded-lg transition"
                style={{
                  borderColor: loginMethod === 'google' ? CSS_VARS.gold : CSS_VARS.creamDark,
                  backgroundColor: loginMethod === 'google' ? CSS_VARS.goldLight : 'transparent',
                  fontFamily: "'Kanit', sans-serif"
                }}
                onMouseEnter={(e) => {
                  if (loginMethod !== 'google') {
                    e.target.style.borderColor = CSS_VARS.gold;
                    e.target.style.backgroundColor = CSS_VARS.cream;
                  }
                }}
                onMouseLeave={(e) => {
                  if (loginMethod !== 'google') {
                    e.target.style.borderColor = CSS_VARS.creamDark;
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className="text-xl md:text-2xl mb-0.5 md:mb-1">📧</div>
                <div className="font-semibold text-xs md:text-sm">Gmail</div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMethod('phone');
                  setOtpSent(false);
                  setOtp('');
                  setConfirmationResult(null);
                }}
                className="p-2 md:p-4 border-2 rounded-lg transition"
                style={{
                  borderColor: loginMethod === 'phone' ? CSS_VARS.gold : CSS_VARS.creamDark,
                  backgroundColor: loginMethod === 'phone' ? CSS_VARS.goldLight : 'transparent',
                  fontFamily: "'Kanit', sans-serif"
                }}
                onMouseEnter={(e) => {
                  if (loginMethod !== 'phone') {
                    e.target.style.borderColor = CSS_VARS.gold;
                    e.target.style.backgroundColor = CSS_VARS.cream;
                  }
                }}
                onMouseLeave={(e) => {
                  if (loginMethod !== 'phone') {
                    e.target.style.borderColor = CSS_VARS.creamDark;
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className="text-xl md:text-2xl mb-0.5 md:mb-1">📱</div>
                <div className="font-semibold text-xs md:text-sm">เบอร์โทร</div>
              </button>
            </div>
          </div>

          {/* Google Login */}
          {loginMethod === 'google' && (
            <div className="p-4 md:p-8 rounded-xl md:rounded-2xl shadow-lg space-y-4 md:space-y-6" style={{ backgroundColor: CSS_VARS.white }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm">
                  {error}
                </div>
              )}

              <div className="text-center">
                <p className="text-sm md:text-base mb-4" style={{ color: '#666' }}>
                  เข้าสู่ระบบด้วยบัญชี Google ของคุณ
                </p>
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full py-3 md:py-4 border-2 rounded-lg font-semibold disabled:cursor-not-allowed transition flex items-center justify-center shadow-sm text-sm md:text-base"
                  style={{
                    backgroundColor: loading ? CSS_VARS.cream : CSS_VARS.white,
                    borderColor: CSS_VARS.creamDark,
                    color: CSS_VARS.greenDeep,
                    fontFamily: "'Kanit', sans-serif"
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = CSS_VARS.cream;
                      e.target.style.borderColor = CSS_VARS.gold;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = CSS_VARS.white;
                      e.target.style.borderColor = CSS_VARS.creamDark;
                    }
                  }}
                >
                  {loading ? (
                    <span style={{ color: '#666' }}>กำลังเข้าสู่ระบบ...</span>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span style={{ color: CSS_VARS.greenDeep }}>เข้าสู่ระบบด้วย Google</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Phone Login */}
          {loginMethod === 'phone' && (
            <div className="p-4 md:p-8 rounded-xl md:rounded-2xl shadow-lg space-y-4 md:space-y-6" style={{ backgroundColor: CSS_VARS.white }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm">
                  {error}
                </div>
              )}

              {!otpSent ? (
                <>
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2" style={{ 
                      color: CSS_VARS.greenDeep,
                      fontFamily: "'Kanit', sans-serif"
                    }}>
                      หมายเลขโทรศัพท์
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-sm md:text-base" style={{ color: '#666' }}>
                        📱
                      </span>
                      <input
                        type="tel"
                        value={formData.emailOrPhone}
                        onChange={(e) => setFormData({ ...formData, emailOrPhone: e.target.value.replace(/\D/g, '') })}
                        placeholder="0812345678"
                        className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-2.5 md:py-3 border rounded-lg focus:ring-2 text-sm md:text-base"
                        style={{
                          backgroundColor: CSS_VARS.white,
                          borderColor: CSS_VARS.creamDark,
                          color: CSS_VARS.black
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = CSS_VARS.gold;
                          e.target.style.boxShadow = `0 0 0 2px ${CSS_VARS.goldLight}`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = CSS_VARS.creamDark;
                          e.target.style.boxShadow = 'none';
                        }}
                        required
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSendOTP}
                    disabled={loading || !formData.emailOrPhone}
                    className="w-full py-3 md:py-4 text-white rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition shadow-md text-sm md:text-base"
                    style={{
                      background: loading || !formData.emailOrPhone ? undefined : `linear-gradient(135deg, ${CSS_VARS.greenDeep} 0%, ${CSS_VARS.greenForest} 100%)`,
                      fontFamily: "'Kanit', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && formData.emailOrPhone) {
                        e.target.style.opacity = '0.9';
                        e.target.style.boxShadow = `0 15px 30px rgba(26, 60, 52, 0.3)`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && formData.emailOrPhone) {
                        e.target.style.opacity = '1';
                        e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    {loading ? 'กำลังส่ง OTP...' : 'ส่ง OTP'}
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1 md:mb-2" style={{ 
                      color: CSS_VARS.greenDeep,
                      fontFamily: "'Kanit', sans-serif"
                    }}>
                      รหัส OTP
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      maxLength={6}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:ring-2 text-center text-xl md:text-2xl tracking-widest"
                      style={{
                        backgroundColor: CSS_VARS.white,
                        borderColor: CSS_VARS.creamDark,
                        color: CSS_VARS.black
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = CSS_VARS.gold;
                        e.target.style.boxShadow = `0 0 0 2px ${CSS_VARS.goldLight}`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = CSS_VARS.creamDark;
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <button
                    onClick={handleVerifyOTP}
                    disabled={loading || otp.length !== 6}
                    className="w-full py-3 md:py-4 text-white rounded-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition shadow-md text-sm md:text-base"
                    style={{
                      background: loading || otp.length !== 6 ? undefined : `linear-gradient(135deg, ${CSS_VARS.greenDeep} 0%, ${CSS_VARS.greenForest} 100%)`,
                      fontFamily: "'Kanit', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && otp.length === 6) {
                        e.target.style.opacity = '0.9';
                        e.target.style.boxShadow = `0 15px 30px rgba(26, 60, 52, 0.3)`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && otp.length === 6) {
                        e.target.style.opacity = '1';
                        e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    {loading ? 'กำลังตรวจสอบ...' : 'ยืนยัน OTP'}
                  </button>

                  <button
                    onClick={() => {
                      setOtpSent(false);
                      setOtp('');
                      setConfirmationResult(null);
                    }}
                    className="w-full py-2 transition text-xs md:text-sm"
                    style={{ color: '#666' }}
                    onMouseEnter={(e) => {
                      e.target.style.color = CSS_VARS.greenDeep;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#666';
                    }}
                  >
                    เปลี่ยนหมายเลขโทรศัพท์
                  </button>

                  {/* Remember Me for Phone Login */}
                  <label className="flex items-center space-x-1.5 md:space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-3.5 h-3.5 md:w-4 md:h-4 rounded"
                      style={{
                        accentColor: CSS_VARS.gold,
                        borderColor: CSS_VARS.creamDark
                      }}
                    />
                    <span className="text-xs md:text-sm" style={{ color: CSS_VARS.greenDeep }}>จดจำเบอร์โทรศัพท์</span>
                  </label>
                </>
              )}
            </div>
          )}

              {/* Footer Link */}
              <div className="text-center mt-4 md:mt-8">
                <p className="text-xs md:text-sm" style={{ color: '#666' }}>
                  ยังไม่มีบัญชี?{' '}
                  <Link 
                    to="/register" 
                    className="font-semibold transition"
                    style={{ color: CSS_VARS.greenDeep }}
                    onMouseEnter={(e) => {
                      e.target.style.color = CSS_VARS.goldDark;
                      e.target.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = CSS_VARS.greenDeep;
                      e.target.style.opacity = '1';
                    }}
                  >
                    สมัครสมาชิก
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginNew;


